// Amazon Transcribe streaming (speech-to-text) for the browser.
//
// This module captures microphone audio via the Web Audio API, converts it to
// 16 kHz / 16-bit little-endian PCM, and streams it to Amazon Transcribe using
// the official AWS SDK v3. The SDK performs the SigV4 "event stream" signing
// (x-amz-content-sha256: STREAMING-AWS4-HMAC-SHA256-EVENTS) and transfer
// encoding required by StartStreamTranscription, so we never hand-roll the
// HTTP/2 request shown in the AWS docs.
//
// Credentials are obtained from an Amazon Cognito Identity Pool, which hands the
// browser short-lived, scoped credentials instead of embedding long-term keys.

import {
  TranscribeStreamingClient,
  StartStreamTranscriptionCommand,
} from "@aws-sdk/client-transcribe-streaming";
import { CognitoIdentityClient } from "@aws-sdk/client-cognito-identity";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";

const TARGET_SAMPLE_RATE = 16000;

// Default silence window before auto-stop (ms); REACT_APP_TRANSCRIBE_SILENCE_TIMEOUT_MS overrides, 0 disables.
const DEFAULT_SILENCE_TIMEOUT_MS = 2500;
// RMS amplitude (on normalized [-1, 1] samples) below which a frame counts as silence.
const SILENCE_RMS_THRESHOLD = 0.01;

/** Reads the Transcribe configuration from CRA environment variables. */
export function getTranscribeConfig() {
  const region = process.env.REACT_APP_AWS_REGION || "us-east-1";
  const silenceRaw = process.env.REACT_APP_TRANSCRIBE_SILENCE_TIMEOUT_MS;
  const parsedSilence = Number(silenceRaw);
  return {
    region,
    language: process.env.REACT_APP_TRANSCRIBE_LANGUAGE || "en-US",
    identityPoolId: process.env.REACT_APP_AWS_IDENTITY_POOL_ID || "",
    identityPoolRegion: process.env.REACT_APP_AWS_IDENTITY_POOL_REGION || region,
    // ms of continuous silence (after speech starts) that triggers auto-stop; 0 disables.
    silenceTimeoutMs:
      silenceRaw === undefined || silenceRaw === "" || !Number.isFinite(parsedSilence)
        ? DEFAULT_SILENCE_TIMEOUT_MS
        : Math.max(0, parsedSilence),
  };
}

/** True when the browser exposes the APIs required for live transcription. */
export function isSpeechToTextSupported() {
  return (
    typeof window !== "undefined" &&
    !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia) &&
    !!(window.AudioContext || window.webkitAudioContext)
  );
}

// Average-pooling downsampler from the microphone's native rate to 16 kHz.
function downsample(buffer, inputSampleRate) {
  if (inputSampleRate === TARGET_SAMPLE_RATE) return buffer;
  const ratio = inputSampleRate / TARGET_SAMPLE_RATE;
  const newLength = Math.round(buffer.length / ratio);
  const result = new Float32Array(newLength);
  let offsetResult = 0;
  let offsetBuffer = 0;
  while (offsetResult < newLength) {
    const nextOffsetBuffer = Math.round((offsetResult + 1) * ratio);
    let accum = 0;
    let count = 0;
    for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i++) {
      accum += buffer[i];
      count += 1;
    }
    result[offsetResult] = count > 0 ? accum / count : 0;
    offsetResult += 1;
    offsetBuffer = nextOffsetBuffer;
  }
  return result;
}

// Float32 [-1, 1] samples -> little-endian signed 16-bit PCM bytes.
function floatTo16BitPcm(input) {
  const view = new DataView(new ArrayBuffer(input.length * 2));
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]));
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return new Uint8Array(view.buffer);
}

// Bridges the pull-based SDK audio stream with the push-based audio callback.
class AudioChunkQueue {
  constructor() {
    this.chunks = [];
    this.resolvers = [];
    this.closed = false;
  }

  push(chunk) {
    if (this.closed) return;
    const resolve = this.resolvers.shift();
    if (resolve) resolve({ value: chunk, done: false });
    else this.chunks.push(chunk);
  }

  close() {
    this.closed = true;
    while (this.resolvers.length) {
      this.resolvers.shift()({ value: undefined, done: true });
    }
  }

  [Symbol.asyncIterator]() {
    return {
      next: () => {
        if (this.chunks.length) {
          return Promise.resolve({ value: this.chunks.shift(), done: false });
        }
        if (this.closed) {
          return Promise.resolve({ value: undefined, done: true });
        }
        return new Promise((resolve) => this.resolvers.push(resolve));
      },
    };
  }
}

/**
 * Starts a live transcription session.
 *
 * @param {object}   opts
 * @param {(r: { transcript: string, isFinal: boolean }) => void} opts.onTranscript
 *        Called with partial (isFinal=false) and finalized (isFinal=true) text.
 * @param {(e: Error) => void} [opts.onError] Called on a fatal stream error.
 * @param {() => void} [opts.onAutoStop] Called after recording auto-stops on silence.
 * @param {object}   [opts.config] Overrides for getTranscribeConfig().
 * @returns {Promise<{ stop: () => Promise<void> }>}
 */
export async function startTranscription({ onTranscript, onError, onAutoStop, config } = {}) {
  const cfg = { ...getTranscribeConfig(), ...config };
  if (!cfg.identityPoolId) {
    throw new Error(
      "Missing REACT_APP_AWS_IDENTITY_POOL_ID. Set a Cognito Identity Pool ID to enable speech-to-text."
    );
  }
  if (!isSpeechToTextSupported()) {
    throw new Error("This browser does not support microphone capture.");
  }

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
    },
  });

  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  const audioContext = new AudioCtx();
  if (audioContext.state === "suspended") {
    try {
      await audioContext.resume();
    } catch {
      /* resume best-effort; capture begins on first audio frame */
    }
  }
  const inputSampleRate = audioContext.sampleRate;
  const source = audioContext.createMediaStreamSource(stream);
  const processor = audioContext.createScriptProcessor(4096, 1, 1);

  const queue = new AudioChunkQueue();
  let stopped = false;

  // Voice-activity auto-stop: once the user has spoken, end the session after
  // `silenceTimeoutMs` of continuous near-silence. Disabled when the value is 0.
  const silenceMs = cfg.silenceTimeoutMs;
  let hasSpoken = false;
  let lastVoiceTs = 0;
  let autoStopped = false;

  const maybeAutoStop = () => {
    if (autoStopped || stopped) return;
    autoStopped = true;
    cleanup().finally(() => onAutoStop?.());
  };

  processor.onaudioprocess = (event) => {
    if (stopped) return;
    const input = event.inputBuffer.getChannelData(0);

    if (silenceMs > 0) {
      let sumSquares = 0;
      for (let i = 0; i < input.length; i++) sumSquares += input[i] * input[i];
      const rms = Math.sqrt(sumSquares / input.length);
      const now = performance.now();
      if (rms >= SILENCE_RMS_THRESHOLD) {
        hasSpoken = true;
        lastVoiceTs = now;
      } else if (hasSpoken && now - lastVoiceTs >= silenceMs) {
        maybeAutoStop();
        return;
      }
    }

    const pcm = floatTo16BitPcm(downsample(input, inputSampleRate));
    queue.push(pcm);
  };

  source.connect(processor);
  processor.connect(audioContext.destination);

  const cleanup = async () => {
    if (stopped) return;
    stopped = true;
    queue.close();
    try {
      processor.disconnect();
      source.disconnect();
    } catch {
      /* already disconnected */
    }
    stream.getTracks().forEach((track) => track.stop());
    try {
      await audioContext.close();
    } catch {
      /* already closed */
    }
  };

  const client = new TranscribeStreamingClient({
    region: cfg.region,
    credentials: fromCognitoIdentityPool({
      client: new CognitoIdentityClient({ region: cfg.identityPoolRegion }),
      identityPoolId: cfg.identityPoolId,
    }),
  });

  async function* audioStream() {
    for await (const chunk of queue) {
      yield { AudioEvent: { AudioChunk: chunk } };
    }
  }

  const command = new StartStreamTranscriptionCommand({
    LanguageCode: cfg.language,
    MediaEncoding: "pcm",
    MediaSampleRateHertz: TARGET_SAMPLE_RATE,
    AudioStream: audioStream(),
  });

  // Consume the response stream in the background; a rejection here means the
  // session ended abnormally (network, credentials, throttling, etc.).
  (async () => {
    try {
      const response = await client.send(command);
      for await (const event of response.TranscriptResultStream) {
        if (stopped) break;
        const results = event.TranscriptEvent?.Transcript?.Results || [];
        for (const result of results) {
          const transcript = result.Alternatives?.[0]?.Transcript;
          if (!transcript) continue;
          onTranscript?.({ transcript, isFinal: !result.IsPartial });
        }
      }
    } catch (err) {
      if (!stopped) onError?.(err instanceof Error ? err : new Error(String(err)));
    } finally {
      await cleanup();
      client.destroy();
    }
  })();

  return {
    stop: async () => {
      await cleanup();
    },
  };
}
